<template>
    <AuthenticationBasic :title="$t('authentication.login')">
        <v-form
                ref="form"
                v-model="valid">
            <v-text-field
                    v-model="email"
                    :label="$t('authentication.email')"
                    :persistent-placeholder="autofillFix"
                    :rules="[$validations.required, $validations.email]"
                    class="mb-2"
                    required
                    @keyup.enter="login"
            ></v-text-field>
            <v-text-field
                    v-model="password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :label="$t('authentication.password')"
                    :persistent-placeholder="autofillFix"
                    :rules="[$validations.required]"
                    :type="showPassword ? 'text' : 'password'"
                    required
                    @keyup.enter="login"
                    @click:append="showPassword = !showPassword"
            ></v-text-field>
            <v-container class="px-2 py-0">
                <v-row class="mb-3">
                    <v-col align-self="center" md="auto">
                        <v-switch
                                v-model="rememberMe"
                                :label="$t('authentication.rememberMe')"
                                color="primary"
                                inset
                        ></v-switch>
                    </v-col>
                    <v-spacer></v-spacer>
                    <v-col align-self="center" md="auto">
                        <router-link :to="{name: 'forgotPassword'}" class="font-weight-bold text--primary fade-weak">
                            {{ $t('authentication.forgotPassword') }}
                        </router-link>
                    </v-col>
                </v-row>
            </v-container>
            <v-btn
                    :loading="loading"
                    block
                    class="elevation-5 mt-5 accentText--text"
                    color="accent"
                    large
                    @click="login">
                {{ $t('authentication.login') }}
            </v-btn>
            <SocialButtons class="mt-8" @google-click="googleLogin" @apple-click="appleLogin"
                           @facebook-click="facebookLogin"/>
            <v-btn
                    block
                    class="mt-8 elevation-5 secondary--text"
                    color="primary"
                    large
                    @click="$router.push({name: 'registration'})">
                {{ $t('authentication.register') }}
            </v-btn>
        </v-form>
    </AuthenticationBasic>
</template>

<script>
import AuthenticationBasic from "@/components/Authentication/AuthenticationBasic";
import {googleProvider, initApple, initFbsdk} from "@/components/Authentication/authentication";
import GoogleSignInButton from 'vue-google-identity-login-btn';
import {GRANT_TYPE_FACEBOOK, GRANT_TYPE_GOOGLE} from "@/helper/constants";
import SocialButtons from "@/components/Authentication/SocialButtons.vue";

export default {
  name: "LoginContent",
  components: {SocialButtons, AuthenticationBasic},
  directives: {
    GoogleSignInButton
  },
  mounted() {
    this.autoLoginCheckingInterface();
    this.initRememberMe();
    this.initFbsdk();
    this.initApple();
  },
  data() {
    return {
      valid: false,
      email: this.$route.params.email ? this.$route.params.email : '',
      password: '',
      rememberMe: true,
      autofillFix: false,
      showPassword: false,
      loading: false,
    };
  },
  watch: {
    rememberMe(val) {
      this.$store.dispatch('setRememberMe', val);
    }
  },
  methods: {
    initFbsdk,
    initApple,
    login() {
      this.$refs.form.validate();
      if (!this.valid) {
        return;
      }
      this.loading = true;
      this.$store.dispatch('login', {
        email: this.email,
        password: this.password,
        rememberMe: this.rememberMe
      }).then(() => {
        this.$router.push({name: 'circleMap'});
      }).catch((err) => {
        let message = '';
        if (err.code === 'ECONNABORTED') {
          message = this.$t('requestTimeout');
        } 
        this.$store.dispatch('addNotification', {
          type: 'error',
          message: err.response?.data?.ErrorMessages || message,
          translate: true,
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    initRememberMe() {
      this.rememberMe = this.$store.getters.isRememberMe;
    },
    autoLoginCheckingInterface() {
      // each 100ms we check if the issue was produced
      let intervalDetectAutofill = setInterval(() => {
        if (
            // we target at least one of the stuff that will be affected by autofill
            // to do our checking
            document.querySelectorAll('input[type="password"]:-webkit-autofill')
                .length > 0
        ) {
          // and we inform the system about the issue if it is produced
          this.autofillFix = true;

          // we stop to check if issue was produced
          clearInterval(intervalDetectAutofill);
        }
      }, 100);

      // if after 3s nothing appear, means no autofill was made
      setTimeout(() => {
        if (intervalDetectAutofill) {
          clearInterval(intervalDetectAutofill);
          intervalDetectAutofill = null;
        }
      }, 3000);
    },
    googleLogin() {
      const login = googleProvider.useGoogleLogin({
        flow: 'implicit',
        onSuccess: (res) => this.googleLoginSuccess(res.access_token),
        onError: (err) => console.error('Failed to login with google', err),
      });
      login();
    },
    async googleLoginSuccess(token) {
      this.loading = true;
      this.$store.dispatch('loginThirdParty', {
        grantType: GRANT_TYPE_GOOGLE,
        accessToken: token,
        rememberMe: this.rememberMe
      }).then((response) => {
        if (response.status === 200) {
          this.$router.push({name: 'circleMap'});
        }

        if (response.message) {
          this.$store.dispatch('addNotification', {
            type: 'error',
            message: response.message,
            translate: false,
          });
        }
      }).finally(() => {
        this.loading = false;
      });

    },
    facebookLogin() {
      window.FB.login((response) => {
        if (response.authResponse) {
          const authToken = response.authResponse.accessToken;
          this.loading = true;
          this.$store.dispatch('loginThirdParty', {
            grantType: GRANT_TYPE_FACEBOOK,
            accessToken: authToken,
            rememberMe: this.rememberMe
          }).then((response) => {
            if (response.status === 200) {
              this.$router.push({name: 'circleMap'});
            }

            if (response.message) {
              this.$store.dispatch('addNotification', {
                type: 'error',
                message: response.message,
                translate: false,
              });
            }
          }).finally(() => {
            this.loading = false;
          });
        }
      }, {scope: 'email public_profile'});
      return false;
    },
    async appleLogin() {
      let response = await window.AppleID.auth.signIn();
      if (!response) {
        return;
      }
      let payload = {
        code: response.authorization.code,
        idToken: response.authorization.id_token,
        rememberMe: this.rememberMe
      };
      if (response.user) {
        payload.email = response.user.email;
        payload.firstName = response.user.name.firstName;
        payload.lastName = response.user.name.lastName;
      }
      this.loading = true;
      this.$store.dispatch('loginApple', payload).then((response) => {
        if (response.status === 200) {
          this.$router.push({name: 'circleMap'});
        }

        if (response.message) {
          this.$store.dispatch('addNotification', {
            type: 'error',
            message: response.message,
            translate: false,
          });
        }
      }).finally(() => {
        this.loading = false;
      });
    },
  }
};
</script>

<style lang="scss" scoped>

</style>